import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  BellIcon,
  CheckCircle2Icon,
  PencilIcon,
  PlusIcon,
  Trash2Icon,
} from "lucide-react";

import {
  useAcknowledgeEvent,
  useAlertEvents,
  useAlertEventsSummary,
  useAlertRules,
  useDeleteRule,
  useResolveEvent,
  useSuppressEvent,
  useToggleRule,
  type AlertEvent,
  type AlertRule,
  type EventStatus,
  type Severity,
  useBulkAcknowledgeEvents,
  useBulkResolveEvents,
} from "@/api/alerts";
import { useStatusFeed } from "@/api/status_feed";
import { useCameras } from "@/api/cameras";
import { useSites } from "@/api/sites";
import { useUsers } from "@/api/users";
import { AlarmVerificationDrawer } from "@/components/AlarmVerificationDrawer";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Timestamp } from "@/components/ui/timestamp";
import { DeviceContextLabel } from "@/components/DeviceContextLabel";
import { QueryState } from "@/components/QueryState";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { ExportCsvButton } from "@/components/ExportCsvButton";
import { makeNameResolver } from "@/lib/resolveNames";
import { shortId, type CameraRef } from "@/types/deviceRef";
import { useAuthStore } from "@/store/auth";
import { cn } from "@/lib/utils";
import { AddAlertRuleDialog } from "./AddAlertRuleDialog";

type Tab = "events" | "rules";

function severityVariant(s: Severity): "error" | "warning" | "online" {
  if (s === "critical") return "error";
  if (s === "warning") return "warning";
  return "online";
}

function statusVariant(s: EventStatus): "error" | "warning" | "online" {
  if (s === "open") return "error";
  if (s === "acknowledged") return "warning";
  return "online";
}

// S12 — top-of-page status pills sourced from /alerts/events/summary.
function SummaryPills() {
  const { data } = useAlertEventsSummary();
  const open = data?.total_open ?? 0;
  const ack = data?.acknowledged ?? 0;
  const critical = data?.open?.critical ?? 0;
  const warning = data?.open?.warning ?? 0;
  return (
    <div className="flex flex-wrap gap-2">
      <span className="flex items-center gap-2 rounded-full border border-red-300 bg-red-50 px-3 py-1 text-xs font-medium text-red-700">
        Open <span className="rounded-full bg-red-600 px-2 text-white">{open}</span>
      </span>
      <span className="flex items-center gap-2 rounded-full border border-amber-300 bg-amber-50 px-3 py-1 text-xs font-medium text-amber-700">
        Acknowledged <span className="rounded-full bg-amber-600 px-2 text-white">{ack}</span>
      </span>
      <span className="flex items-center gap-2 rounded-full border border-red-400 bg-red-100 px-3 py-1 text-xs font-medium text-red-900">
        Critical <span className="rounded-full bg-red-700 px-2 text-white">{critical}</span>
      </span>
      <span className="flex items-center gap-2 rounded-full border border-orange-300 bg-orange-50 px-3 py-1 text-xs font-medium text-orange-700">
        Warning <span className="rounded-full bg-orange-600 px-2 text-white">{warning}</span>
      </span>
    </div>
  );
}


function SuppressDialog({
  evt, onClose,
}: {
  evt: AlertEvent | null;
  onClose: () => void;
}) {
  const suppress = useSuppressEvent();
  const [until, setUntil] = useState(() => {
    const d = new Date(Date.now() + 60 * 60 * 1000); // +1h default
    return d.toISOString().slice(0, 16);
  });
  if (!evt) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <Card className="w-full max-w-md">
        <CardContent className="space-y-3 p-4">
          <h3 className="text-lg font-semibold">Suppress alert</h3>
          <p className="text-sm text-muted-foreground">{evt.title}</p>
          <div className="space-y-1">
            <label className="text-xs">Suppress until</label>
            <input
              type="datetime-local"
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
              value={until}
              onChange={(e) => setUntil(e.target.value)}
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
            <Button
              size="sm"
              disabled={suppress.isPending}
              onClick={async () => {
                await suppress.mutateAsync({
                  id: evt.id,
                  suppress_until: new Date(until).toISOString(),
                });
                onClose();
              }}
            >Suppress</Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function EventRow({
  evt, camera, siteName, resolveActor, onSuppress, onOpen, selected, onToggle,
}: {
  evt: AlertEvent;
  // D1 — explicit selection. No select-all-across-pages: the checkbox governs
  // exactly the rows on screen, matching the CSV export decision so both mean
  // "what the operator is looking at".
  selected: boolean;
  onToggle: (id: string) => void;
  // Batch 1 (UX/IA) — resolved by the parent: the camera (with NVR context),
  // the site name, and an id→display-name resolver for the audit trail actors.
  camera?: CameraRef;
  siteName?: string;
  resolveActor: (id: string | null | undefined) => string;
  onSuppress: (e: AlertEvent) => void;
  onOpen: (e: AlertEvent) => void;
}) {
  const ack = useAcknowledgeEvent();
  const resolve = useResolveEvent();
  return (
    <>
      <tr
        className="cursor-pointer border-b last:border-0 hover:bg-muted/30"
        onClick={() => onOpen(evt)}
      >
        {/* stopPropagation: the row itself opens the verify drawer, and
            selecting must not also open it. */}
        <td className="px-2 py-2" onClick={(e) => e.stopPropagation()}>
          <input
            type="checkbox"
            aria-label={`Select alert: ${evt.title}`}
            checked={selected}
            onChange={() => onToggle(evt.id)}
          />
        </td>
        <td className="px-4 py-2">
          <Badge variant={severityVariant(evt.severity)}>{evt.severity}</Badge>
        </td>
        <td className="px-4 py-2 font-medium">
          <span>{evt.title}</span>
          {evt.runbook_id && (
            <Badge variant="online" className="ml-2 align-middle">Runbook</Badge>
          )}
          {evt.auto_clip_id && (
            <Badge variant="warning" className="ml-2 align-middle">Clip</Badge>
          )}
        </td>
        <td className="px-4 py-2">
          {camera ? (
            <div className="space-y-0.5">
              <Link
                to={`/cameras/${camera.id}`}
                className="font-medium hover:underline"
                onClick={(e) => e.stopPropagation()}
              >
                {camera.name ?? "Camera"}
              </Link>
              <DeviceContextLabel camera={camera} variant="compact" className="max-w-[16rem]" />
            </div>
          ) : evt.site_id ? (
            <span className="text-sm">{siteName ?? "Site"}</span>
          ) : (
            <span className="text-xs text-muted-foreground">—</span>
          )}
        </td>
        <td className="px-4 py-2 text-xs text-muted-foreground">
          <Timestamp value={evt.created_at} />
        </td>
        <td className="px-4 py-2">
          <Badge variant={statusVariant(evt.status)}>{evt.status}</Badge>
          {/* Audit trail — who/when the alert was ack'd / resolved. */}
          {evt.acknowledged_at && (
            <div className="mt-1 text-[11px] text-muted-foreground">
              ack {resolveActor(evt.acknowledged_by)} ·{" "}
              <Timestamp value={evt.acknowledged_at} format="relative" />
            </div>
          )}
          {evt.resolved_at && (
            <div className="text-[11px] text-muted-foreground">
              resolved {resolveActor(evt.resolved_by)} ·{" "}
              <Timestamp value={evt.resolved_at} format="relative" />
            </div>
          )}
        </td>
        <td className="px-4 py-2 text-right">
          <div className="flex justify-end gap-2">
            {evt.status === "open" && (
              <Button
                size="sm"
                variant="outline"
                disabled={ack.isPending}
                onClick={(e) => {
                  e.stopPropagation();
                  ack.mutate(evt.id);
                }}
              >
                Acknowledge
              </Button>
            )}
            {evt.status !== "resolved" && (
              <Button
                size="sm"
                variant="outline"
                disabled={resolve.isPending}
                onClick={(e) => {
                  e.stopPropagation();
                  resolve.mutate(evt.id);
                }}
              >
                Resolve
              </Button>
            )}
            {evt.status === "open" && (
              <Button
                size="sm"
                variant="outline"
                onClick={(e) => {
                  e.stopPropagation();
                  onSuppress(evt);
                }}
              >
                Suppress
              </Button>
            )}
          </div>
        </td>
      </tr>
    </>
  );
}

function EventsTab() {
  const [severity, setSeverity] = useState<Severity | "">("");
  const [statusF, setStatusF] = useState<EventStatus | "">("");
  const [suppressTarget, setSuppressTarget] = useState<AlertEvent | null>(null);
  const [verifyTarget, setVerifyTarget] = useState<AlertEvent | null>(null);
  const { data, isLoading, isError, error, refetch } = useAlertEvents({
    severity: (severity || undefined) as Severity | undefined,
    status: (statusF || undefined) as EventStatus | undefined,
  });

  // Batch 1 (UX/IA) — resolve alert references to human context (name + NVR
  // hierarchy) instead of truncated UUIDs, and the ack/resolve actors to names.
  const cameras = useCameras({ limit: 200 });
  const sites = useSites();
  const users = useUsers();
  const cameraById = useMemo(() => {
    const m = new Map<string, CameraRef>();
    for (const c of cameras.data?.items ?? []) m.set(c.id, c);
    return m;
  }, [cameras.data]);
  const resolveSite = useMemo(
    () => makeNameResolver(sites.data?.items),
    [sites.data],
  );
  const resolveActor = useMemo(() => {
    // UserDetail carries full_name?/email — prefer name, fall back to email,
    // then a short id (users list may be empty for non-admins → graceful).
    const byId = new Map<string, string>();
    for (const u of users.data?.items ?? []) {
      byId.set(u.id, u.full_name || u.email);
    }
    return (id: string | null | undefined) =>
      id ? byId.get(id) ?? shortId(id) : "";
  }, [users.data]);

  // T4c — export the rows the operator is looking at, with their filters
  // applied. A server export that ignored the on-screen severity/status
  // filters would hand back a different dataset than the screen shows, which
  // is the disparity class this audit is about.
  const exportRows = data?.items ?? [];

  // D1 — bulk acknowledge / resolve. SAME selection scope as the export above:
  // what is on screen, nothing more. Two mechanisms that both claim to act on
  // "the current view" must not disagree about what that means.
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [confirm, setConfirm] = useState<null | "acknowledge" | "resolve">(null);
  const [bulkNote, setBulkNote] = useState<string | null>(null);
  const bulkAck = useBulkAcknowledgeEvents();
  const bulkResolve = useBulkResolveEvents();
  const busy = bulkAck.isPending || bulkResolve.isPending;

  const pageIds = useMemo(() => exportRows.map((e) => e.id), [exportRows]);
  // Drop selections for rows that have left the view (filter change, refetch),
  // so the count on the button can never exceed what is visible.
  useEffect(() => {
    setSelectedIds((prev) => {
      const live = new Set(pageIds);
      const next = new Set([...prev].filter((id) => live.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [pageIds]);

  const toggleSelected = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);
  const allOnPageSelected =
    pageIds.length > 0 && pageIds.every((id) => selectedIds.has(id));
  const toggleAllOnPage = useCallback(() => {
    setSelectedIds((prev) =>
      pageIds.every((id) => prev.has(id)) ? new Set() : new Set(pageIds),
    );
  }, [pageIds]);

  async function runBulk(action: "acknowledge" | "resolve") {
    const ids = [...selectedIds];
    setConfirm(null);
    const res = action === "acknowledge"
      ? await bulkAck.mutateAsync(ids)
      : await bulkResolve.mutateAsync(ids);
    // Report the REAL outcome. "7 of 9 acknowledged" is the honest sentence
    // when two failed; a bare success toast would be the false-green this
    // programme has been removing all along.
    setBulkNote(
      res.failed.length === 0
        ? `${res.succeeded.length} alert${res.succeeded.length === 1 ? "" : "s"} ${action === "acknowledge" ? "acknowledged" : "resolved"}.`
        : `${res.succeeded.length} of ${ids.length} ${action === "acknowledge" ? "acknowledged" : "resolved"} — ${res.failed.length} failed and were left unchanged.`,
    );
    setSelectedIds(new Set());
  }

  return (
    <div className="space-y-4">
      {/* D1 — the bulk bar appears ONLY when something is selected, and it
          always states the count. Nothing destructive lives here: acknowledge
          and resolve are both reversible states on an alert, which is why they
          are the two verbs approved for bulk. */}
      {selectedIds.size > 0 && (
        <Card>
          <CardContent className="flex flex-wrap items-center gap-3 p-3">
            <span className="text-sm font-medium">
              {selectedIds.size} selected
            </span>
            <Button
              size="sm"
              variant="outline"
              disabled={busy}
              onClick={() => setConfirm("acknowledge")}
            >
              Acknowledge {selectedIds.size}
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={busy}
              onClick={() => setConfirm("resolve")}
            >
              Resolve {selectedIds.size}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              disabled={busy}
              onClick={() => setSelectedIds(new Set())}
            >
              Clear
            </Button>
            <span className="text-xs text-muted-foreground">
              acts on the {selectedIds.size} selected row
              {selectedIds.size === 1 ? "" : "s"} on this page only
            </span>
          </CardContent>
        </Card>
      )}

      {bulkNote && (
        <Card>
          <CardContent className="flex items-center justify-between gap-3 p-3">
            <span className="text-sm">{bulkNote}</span>
            <Button size="sm" variant="ghost" onClick={() => setBulkNote(null)}>
              Dismiss
            </Button>
          </CardContent>
        </Card>
      )}

      <ConfirmDialog
        open={confirm !== null}
        onOpenChange={(o) => !o && setConfirm(null)}
        tone="default"
        title={
          confirm === "resolve"
            ? `Resolve ${selectedIds.size} alert${selectedIds.size === 1 ? "" : "s"}?`
            : `Acknowledge ${selectedIds.size} alert${selectedIds.size === 1 ? "" : "s"}?`
        }
        description={
          confirm === "resolve"
            ? `This marks ${selectedIds.size} selected alert${selectedIds.size === 1 ? "" : "s"} resolved. Each is recorded separately in the audit log against you.`
            : `This acknowledges ${selectedIds.size} selected alert${selectedIds.size === 1 ? "" : "s"}. Each is recorded separately in the audit log against you.`
        }
        confirmLabel={confirm === "resolve" ? "Resolve" : "Acknowledge"}
        busy={busy}
        onConfirm={() => confirm && runBulk(confirm)}
      />

      <div className="flex justify-end">
        <ExportCsvButton
          rows={exportRows}
          filename="alert-events"
          columns={[
            { header: "Created", value: (e) => e.created_at ?? "" },
            { header: "Severity", value: (e) => e.severity ?? "" },
            { header: "Status", value: (e) => e.status ?? "" },
            { header: "Title", value: (e) => e.title ?? "" },
            { header: "Message", value: (e) => e.message ?? "" },
            {
              header: "Camera",
              value: (e) =>
                e.camera_id ? cameraById.get(e.camera_id)?.name ?? e.camera_id : "",
            },
            { header: "Rule type", value: (e) => e.rule_type ?? "" },
            { header: "Alert type", value: (e) => e.alert_type ?? "" },
            { header: "Escalation level", value: (e) => e.escalation_level ?? 0 },
            { header: "Acknowledged by", value: (e) => resolveActor(e.acknowledged_by) },
            { header: "Resolved at", value: (e) => e.resolved_at ?? "" },
          ]}
        />
      </div>
      <Card>
        <CardContent className="grid grid-cols-2 gap-3 p-4 md:grid-cols-4">
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">Severity</label>
            <select
              value={severity}
              onChange={(e) => setSeverity(e.target.value as Severity | "")}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
            >
              <option value="">All severities</option>
              <option value="critical">Critical</option>
              <option value="warning">Warning</option>
              <option value="info">Info</option>
            </select>
          </div>
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">Status</label>
            <select
              value={statusF}
              onChange={(e) => setStatusF(e.target.value as EventStatus | "")}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
            >
              <option value="">All statuses</option>
              <option value="open">Open</option>
              <option value="acknowledged">Acknowledged</option>
              <option value="resolved">Resolved</option>
            </select>
          </div>
        </CardContent>
      </Card>

      {/* AUDIT T1a — a failed fetch used to render NOTHING here: isLoading went
          false, data stayed undefined, and neither branch below matched. The
          page showed filters over a void. Worse, the empty state below says
          "all systems healthy" — so a 500 rendered as REASSURANCE. */}
      {isError && (
        <QueryState
          isLoading={false}
          isError
          error={error}
          label="alerts"
          onRetry={refetch}
        >
          {null}
        </QueryState>
      )}

      {isLoading && !isError && <p className="text-sm">Loading…</p>}

      {!isError && data && data.items.length === 0 && (
        <Card>
          <CardContent className="space-y-2 py-12 text-center">
            <CheckCircle2Icon className="mx-auto h-10 w-10 text-emerald-500" />
            <p className="text-sm">No alerts — all systems healthy ✓</p>
          </CardContent>
        </Card>
      )}

      {!isError && data && data.items.length > 0 && (
        <Card>
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead className="border-b bg-muted/50 text-xs uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="w-8 px-2 py-3 text-left">
                    <input
                      type="checkbox"
                      aria-label="Select all alerts on this page"
                      checked={allOnPageSelected}
                      // Governs THIS PAGE only. There is deliberately no
                      // select-all-across-pages: an operator cannot review what
                      // they cannot see, and a bulk mutation over unseen rows is
                      // exactly the kind of action that gets regretted.
                      onChange={toggleAllOnPage}
                    />
                  </th>
                  <th className="px-4 py-3 text-left">Severity</th>
                  <th className="px-4 py-3 text-left">Title</th>
                  <th className="px-4 py-3 text-left">Camera / Site</th>
                  <th className="px-4 py-3 text-left">Time</th>
                  <th className="px-4 py-3 text-left">Status</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((evt) => (
                  <EventRow
                    key={evt.id}
                    evt={evt}
                    camera={evt.camera_id ? cameraById.get(evt.camera_id) : undefined}
                    siteName={evt.site_id ? resolveSite(evt.site_id) : undefined}
                    resolveActor={resolveActor}
                    onSuppress={setSuppressTarget}
                    onOpen={setVerifyTarget}
                    selected={selectedIds.has(evt.id)}
                    onToggle={toggleSelected}
                  />
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}
      <SuppressDialog evt={suppressTarget} onClose={() => setSuppressTarget(null)} />
      <AlarmVerificationDrawer
        alertId={verifyTarget?.id ?? null}
        open={!!verifyTarget}
        onClose={() => setVerifyTarget(null)}
      />
    </div>
  );
}

function RuleCard({
  rule,
  onEdit,
}: {
  rule: AlertRule;
  onEdit: (r: AlertRule) => void;
}) {
  const toggle = useToggleRule();
  const del = useDeleteRule();
  return (
    <Card>
      <CardContent className="space-y-3 p-4">
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2">
              <p className="font-medium">{rule.name}</p>
              <Badge variant={severityVariant(rule.severity)}>
                {rule.severity}
              </Badge>
              <Badge variant={rule.enabled ? "online" : "offline"}>
                {rule.enabled ? "enabled" : "disabled"}
              </Badge>
              {/* A soft-accepted ("C-soft") rule_type is stored and reads
                  "enabled" but engine_v2 has no evaluator, so it can never
                  fire. Without this badge it is indistinguishable from a
                  working rule — the same report-that-reaches-nobody shape as a
                  critical rule with no channel. */}
              {rule.engine_supported === false && (
                <Badge variant="offline" title="No evaluator in the alert engine — this rule will never fire">
                  never fires
                </Badge>
              )}
            </div>
            <p className="mt-1 font-mono text-xs text-muted-foreground">
              {rule.rule_type} · cooldown {rule.cooldown_minutes}m
              {rule.notify_email ? " · email" : ""}
              {rule.notify_webhook ? " · webhook" : ""}
            </p>
            {Object.keys(rule.condition_params).length > 0 && (
              <p className="mt-1 font-mono text-[11px] text-muted-foreground">
                {JSON.stringify(rule.condition_params)}
              </p>
            )}
          </div>
          <div className="flex gap-1">
            <Button
              size="sm"
              variant="outline"
              onClick={() => toggle.mutate(rule.id)}
            >
              {rule.enabled ? "Disable" : "Enable"}
            </Button>
            <Button size="sm" variant="outline" onClick={() => onEdit(rule)}>
              <PencilIcon className="h-3.5 w-3.5" />
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={async () => {
                if (!confirm(`Delete rule "${rule.name}"?`)) return;
                await del.mutateAsync(rule.id);
              }}
            >
              <Trash2Icon className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function RulesTab() {
  const { data } = useAlertRules();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<AlertRule | null>(null);

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button
          onClick={() => {
            setEditing(null);
            setOpen(true);
          }}
        >
          <PlusIcon className="mr-2 h-4 w-4" />
          Add rule
        </Button>
      </div>
      <div className="space-y-3">
        {(data?.items ?? []).map((rule) => (
          <RuleCard
            key={rule.id}
            rule={rule}
            onEdit={(r) => {
              setEditing(r);
              setOpen(true);
            }}
          />
        ))}
        {data && data.items.length === 0 && (
          <Card>
            <CardContent className="py-10 text-center text-sm text-muted-foreground">
              No alert rules yet.
            </CardContent>
          </Card>
        )}
      </div>
      <AddAlertRuleDialog
        open={open}
        onOpenChange={(o) => {
          setOpen(o);
          if (!o) setEditing(null);
        }}
        rule={editing}
      />
    </div>
  );
}

export function AlertsPage() {
  const [tab, setTab] = useState<Tab>("events");
  // Subscribe to the realtime feed so React-Query caches stay live.
  useStatusFeed();
  const role = useAuthStore((s) => s.user?.role);
  const canManageRules = role === "admin";

  return (
    <div className="space-y-6">
      <div>
        <h1 className="flex items-center gap-2 text-2xl font-semibold tracking-tight">
          <BellIcon className="h-6 w-6" />
          Alerts
        </h1>
        <p className="text-sm text-muted-foreground">
          Tamper-evident alerts emitted by the engine.
        </p>
      </div>

      <SummaryPills />

      <div className="flex gap-2 border-b">
        <button
          onClick={() => setTab("events")}
          className={cn(
            "px-3 py-2 text-sm",
            tab === "events"
              ? "border-b-2 border-primary font-medium text-foreground"
              : "text-muted-foreground hover:text-foreground",
          )}
        >
          Events
        </button>
        {canManageRules && (
          <button
            onClick={() => setTab("rules")}
            className={cn(
              "px-3 py-2 text-sm",
              tab === "rules"
                ? "border-b-2 border-primary font-medium text-foreground"
                : "text-muted-foreground hover:text-foreground",
            )}
          >
            Rules
          </button>
        )}
      </div>

      {tab === "events" && <EventsTab />}
      {tab === "rules" && canManageRules && <RulesTab />}
    </div>
  );
}
